import { createContext, useContext, type PointerEvent as ReactPointerEvent } from 'react';
import type { BoardDrawTool, BoardDrawing } from '../../types';
import type { DrawingSelection } from './BoardDrawLayer';

/**
 * Actions exposed to custom board nodes so they can mutate the single source of
 * truth held by `InspirationBoard` (which in turn drives autosave).
 */
export interface BoardActions {
  readOnly: boolean;
  drawTool: BoardDrawTool;
  selectedDrawing: DrawingSelection | null;
  selectDrawing: (sel: DrawingSelection | null) => void;
  startDrawingEdit: (drawing: BoardDrawing, target: string, e: ReactPointerEvent) => void;
  updateNodeData: (id: string, patch: Record<string, unknown>) => void;
  deleteNode: (id: string) => void;
  duplicateNode: (id: string) => void;
  bringToFront: (id: string) => void;
  sendToBack: (id: string) => void;
  rotateImage: (id: string) => void;
  groupSelection: () => void;
  ungroupSelection: () => void;
  canGroup: () => boolean;
  canUngroup: () => boolean;
  editTimeline: (id: string) => void;
  editFloorPlan: (id: string) => void;
}

export const BoardActionsContext = createContext<BoardActions | null>(null);

export function useBoardActions(): BoardActions {
  const ctx = useContext(BoardActionsContext);
  if (!ctx) {
    throw new Error('useBoardActions must be used within an InspirationBoard');
  }
  return ctx;
}
